import { useRef } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { Plugin } from '@tiptap/pm/state';
import { checkPortalAccess } from '../../utils/portalAccessUtil';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

const DRAG_THRESHOLD = 5; //px

const PortalLinkComponent = props => {
    const navigate = useNavigate();
    const location = useLocation();

    const portalId = props.node.attrs.portalId;
    const interactive = props.extension.options.interactive;
    const onBlockedClick = props.extension.options.onBlockedClick;

    const navigationStack = props.extension.options.navigationStack || [];
    const parentPortalId = props.extension.options.parentPortalId || null;

    const dragging = useRef(false);
    const startPos = useRef({ x: 0, y: 0 });

    const onMouseDown = e => {
        dragging.current = false;
        startPos.current = { x: e.clientX, y: e.clientY };
    };

    const onMouseMove = e => {
        if (!startPos.current) return;
        const dx = Math.abs(e.clientX - startPos.current.x);
        const dy = Math.abs(e.clientY - startPos.current.y);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
            dragging.current = true;
        }
    };

    const navigateToChildPortal = async childPortalId => {
        try {
            const routePrefix =
                props.extension.options.routePrefix || '/portal-pages';

            await checkPortalAccess({
                portalId: childPortalId,
                isViewLive: routePrefix === '/view-live',
            });

            const baseStack =
                Array.isArray(navigationStack) && navigationStack.length
                    ? [...navigationStack]
                    : parentPortalId
                      ? [parentPortalId]
                      : [];
            const homeId =
                baseStack.length > 0 ? baseStack[0] : parentPortalId || null;

            try {
                if (
                    (location.pathname === '/' || location.pathname === '') &&
                    homeId !== null
                ) {
                    const newHomeState = {
                        ...(location.state || {}),
                        selectedTabId: homeId,
                    };
                    navigate(location.pathname || '/', {
                        replace: true,
                        state: newHomeState,
                    });
                }
            } catch (e) {
                console.warn('Failed to annotate history state', e);
            }

            const newBack = [...baseStack, childPortalId];
            try {
                sessionStorage.setItem(
                    'navigationStack:back',
                    JSON.stringify(newBack),
                );
            } catch (e) {
                console.warn('Failed to set navigationStack:back', e);
            }

            navigate(`${routePrefix}/${childPortalId}`, {
                state: {
                    navigationStack: newBack,
                },
            });
        } catch (error) {
            if (error?.code === 'NO_PORTAL_ACCESS') {
                Toast.info(
                    getText('msg_b8c3f507d91a'),
                    {},
                    getText('msg_13d4498a0dcc'),
                );
                return;
            }

            console.error('navigateToChildPortal failed:', error);
            Toast.error(getText('msg_fbb9c884f239'));
        }
    };

    const onClick = e => {
        if (dragging.current) {
            return;
        }

        if (!portalId) return;

        e.preventDefault();
        e.stopPropagation();

        if (interactive) {
            navigateToChildPortal(Number(portalId));
        } else {
            onBlockedClick?.(portalId);
        }
    };

    const onKeyDown = e => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (interactive) {
                navigateToChildPortal(Number(portalId));
            } else {
                onBlockedClick?.(portalId);
            }
        }
    };

    const hasClickHandler = interactive || typeof onBlockedClick === 'function';

    return (
        <NodeViewWrapper
            as="span"
            data-portal-link="true"
            data-portal-id={portalId}
            role="link"
            tabIndex={0}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onClick={onClick}
            onKeyDown={onKeyDown}
            style={{
                cursor: hasClickHandler ? 'pointer' : 'default',
                textDecoration: 'underline',
                color: '#1366ff',
                pointerEvents: 'auto',
                userSelect: 'text',
            }}
        >
            <NodeViewContent as="span" />
        </NodeViewWrapper>
    );
};

export const PortalLinkNode = Node.create({
    name: 'portalLink',

    inline: true,
    group: 'inline',
    selectable: true,
    atom: false,
    content: getText('msg_1e97f5db6430'),

    addAttributes() {
        return {
            portalId: {
                default: null,
            },
        };
    },

    addOptions() {
        return {
            interactive: true,
            onBlockedClick: null,
            navigationStack: [],
            parentPortalId: null,
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-portal-link="true"]',
                priority: 1000,
                getAttrs: dom => {
                    if (!dom || typeof dom.getAttribute !== 'function')
                        return false;
                    return {
                        portalId: dom.getAttribute('data-portal-id'),
                    };
                },
            },
            {
                // Content headed outside the app stores portal links as anchors so they stay clickable there.
                tag: 'a[data-portal-link="true"]',
                priority: 1000,
                getAttrs: dom => {
                    if (!dom || typeof dom.getAttribute !== 'function')
                        return false;

                    const portalId = dom.getAttribute('data-portal-id');
                    return portalId ? { portalId } : false;
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-portal-link': 'true',
                'data-portal-id': HTMLAttributes.portalId,
                role: 'link',
                tabIndex: '0',
                style: 'cursor:pointer;pointer-events:auto;color:#1366ff;decoration:underline',
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PortalLinkComponent);
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                appendTransaction: (transactions, oldState, newState) => {
                    if (!transactions.some(t => t.docChanged)) return null;

                    let tr = newState.tr;
                    let changed = false;

                    newState.doc.descendants((node, pos) => {
                        if (
                            node.type.name === this.name &&
                            node.content.size === 0
                        ) {
                            tr.delete(pos, pos + node.nodeSize);
                            changed = true;
                            return false;
                        }
                        return true;
                    });
                    return changed ? tr : null;
                },
            }),
        ];
    },

    addCommands() {
        return {
            setPortalLink:
                attributes =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: attributes,
                        content: [
                            { type: 'text', text: attributes?.text ?? '' },
                        ],
                    });
                },
            unsetPortalLink:
                () =>
                ({ state, dispatch }) => {
                    const { from, to } = state.selection;
                    if (dispatch) {
                        const tr = state.tr.delete(from, to);
                        dispatch(tr);
                    }
                    return true;
                },
        };
    },
});
